import { ComponentDefinition } from '../base-definitions'
import { BreacrumbDefinition } from '../breadcrumb-definition'
import { CustomActionDefinition } from '../custom-action-definitions'
import { FormOpenMode, ModalSize, OpenMode, ViewPinnedDefinition, VisualizationMode } from '../enums'
import { FieldDefinition } from '../field-definitions'
import { FormViewDefinition, TooltipDefinition } from '../form-definitions'
import { DefinitionUtils } from '../utils'
import { CountBehaviour } from './count-behaviour'
import { EmptyStateBehaviourDefinition } from './empty-state-behaviour-definition'
import { FilterCollection } from './filters'
import { ViewHeaderDefinition } from './header'
import { BaseHeaderLinkDefinition } from './header-links'
import { ViewSubtitleDefinition } from './header-subtitile-definition'
import { BaseJoinDefinition } from './join-definition'
import { ModalForm } from './modal-form'
import { OrderByDefinition } from './order-by-definition'
import { RowActionsDefinition } from './row-actions'
import { RowClickModeDefiniton } from './row-click'
import { ViewColumnDefinition } from './view-column'
import { ViewExtraDefinition } from './view-extra'
import { ViewLabelDefinition } from './view-labels-definition'
import { CompositeConditionDefinition } from './where-definition'

export class ViewModel {
  public name: string
  public entity: string
  public associatedSiteMap: string
  public idml: string
  public description: string
  public defaultColumnStyle: ViewColumnDefinition
  public columns: ViewColumnDefinition[]
  public joins: BaseJoinDefinition[]
  public filters: FilterCollection
  public where: CompositeConditionDefinition
  public order: OrderByDefinition[]
  public customActions: CustomActionDefinition[]
  public visualization: VisualizationMode
  public openMode: OpenMode
  public modalSize: ModalSize | string
  public modalSizeHorizontal: ModalSize
  public modalSizeVertical: ModalSize
  public canBeCreated: boolean
  public canEdit: boolean
  public multiSelection: boolean
  public selectAll: boolean
  public openFormModel: string
  public pivotBy: string
  public pivotHideEmptyRows: boolean
  public formOpenMode: FormOpenMode
  public hideHeader: boolean
  public hideDescription: boolean
  public hideColumnHeader: boolean
  public hideColumnHeaderSubtitle: boolean
  public hidePagination: boolean
  public pageSize: number
  public componentDefinition?: ComponentDefinition
  public detailView?: FormViewDefinition
  public rowActions: RowActionsDefinition
  public breadcrumb: BreacrumbDefinition
  public emptyStateBehaviour?: EmptyStateBehaviourDefinition
  public headerLinks: BaseHeaderLinkDefinition[]
  public labels: ViewLabelDefinition
  public modalForm: ModalForm
  public pinned: ViewPinnedDefinition
  public extra?: ViewExtraDefinition
  public tooltip?: TooltipDefinition
  public subtitle?: ViewSubtitleDefinition
  public rowClickMode: RowClickModeDefiniton
  public countBehaviour: CountBehaviour
  public header: ViewHeaderDefinition

  constructor(model: ViewModel) {
    this.name = model.name
    this.entity = model.entity
    this.idml = model.idml
    this.description = model.description
    this.defaultColumnStyle = model.defaultColumnStyle
    this.joins = model.joins
    this.where = model.where
    this.filters = new FilterCollection(model.filters)
    this.order = model.order
    this.associatedSiteMap = model.associatedSiteMap
    this.visualization = model.visualization
    this.customActions = model.customActions ? this.mapCustomActions(model.customActions) : []
    this.openMode = model.openMode
    this.modalSize = model.modalSize
    this.modalSizeHorizontal = model.modalSizeHorizontal
    this.modalSizeVertical = model.modalSizeVertical
    this.canBeCreated = model.canBeCreated
    this.canEdit = model.canEdit
    this.multiSelection = model.multiSelection
    this.selectAll = model.selectAll
    this.openFormModel = model.openFormModel
    this.pivotBy = model.pivotBy
    this.pivotHideEmptyRows = model.pivotHideEmptyRows
    this.formOpenMode = model.formOpenMode
    this.hideDescription = model.hideDescription
    this.hideHeader = model.hideHeader
    this.hideColumnHeader = model.hideColumnHeader
    this.hideColumnHeaderSubtitle = model.hideColumnHeaderSubtitle
    this.hidePagination = model.hidePagination
    this.pageSize = model.pageSize
    this.componentDefinition = model.componentDefinition
    this.detailView = model.detailView
    this.rowActions = model.rowActions
    this.breadcrumb = model.breadcrumb
    this.emptyStateBehaviour = model.emptyStateBehaviour
    this.headerLinks = model.headerLinks ?? []
    this.labels = model.labels
    this.modalForm = model.modalForm
    this.pinned = model.pinned
    this.extra = undefined
    this.subtitle = model.subtitle
    this.countBehaviour = model.countBehaviour
    this.header = model.header
    if (model.extra) {
      this.extra = new ViewExtraDefinition(model.extra)
    }
    this.tooltip = model.tooltip
    this.rowClickMode = model.rowClickMode
    this.columns = []
    if (model.columns) {
      model.columns.forEach((col) => {
        this.columns.push(new ViewColumnDefinition(col))
      })
    }
  }

  public getTableAliasForColumn(viewColumnName: string): string {
    if (viewColumnName.indexOf('.')) {
      return viewColumnName.split('.')[0]
    } else {
      return this.entity
    }
  }

  public getFieldNameForColumn(viewColumnName: string): string {
    if (viewColumnName.indexOf('.') != -1) {
      return viewColumnName.split('.')[1]
    } else {
      return viewColumnName
    }
  }

  public getEntityNameForColumn(columnFieldName: string): string {
    const alias = this.getTableAliasForColumn(columnFieldName)
    if (alias.toLowerCase() === this.entity.toLowerCase()) {
      return this.entity
    } else if (this.joins) {
      for (let i = 0; i < this.joins.length; i++) {
        const join = this.joins[i]
        if (join.as === alias) return join.entity
      }
    }
    return this.entity
  }

  private mapCustomActions(customActions: CustomActionDefinition[]): CustomActionDefinition[] {
    const mappedActions: CustomActionDefinition[] = []
    if (customActions) {
      customActions.forEach((action) => {
        mappedActions.push(new CustomActionDefinition(action))
      })
    }
    return mappedActions
  }

  public getTypedDefinitionForColumn(fieldName: string): FieldDefinition {
    let fieldDef: FieldDefinition | null = null
    const columnDef: ViewColumnDefinition = this.columns.find(
      (col) => col.fieldName == fieldName,
    ) as ViewColumnDefinition

    if (columnDef && columnDef.inlineDefinition) {
      fieldDef = DefinitionUtils.mapFieldDefinition(columnDef.inlineDefinition)
    }

    return fieldDef as FieldDefinition
  }
}
